import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { api } from '../services/api';
import { MultiWalletContext } from '../contexts/MultiWalletContext';
import PositionCard, { Position } from '../components/positions/PositionCard';
import PositionSummary from '../components/positions/PositionSummary';
import PositionHistory from '../components/positions/PositionHistory';
import ClosePositionModal from '../components/positions/ClosePositionModal';
import EditSlModal from '../components/positions/EditSlModal';
import '../components/positions/positions.css';

type TabFilter = 'ALL' | 'HYPERLIQUID' | 'BINANCE' | 'BITGET' | 'ASTER' | 'LIGHTER' | 'GMX';

const REFRESH_INTERVAL = 10000; // 10s

export default function PositionsPage() {
  const { isAuthenticated } = useContext(MultiWalletContext);
  const [positions, setPositions] = useState<Position[]>([]);
  const [summary, setSummary] = useState<{ total_positions: number; total_unrealized_pnl: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabFilter>('ALL');
  const [view, setView] = useState<'open' | 'history'>('open');
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL / 1000);

  const [closeTarget, setCloseTarget] = useState<Position | null>(null);
  const [slTarget, setSlTarget] = useState<Position | null>(null);

  // Binance/Bitget key status (check if user has bound API key)
  const [hasApiKeys, setHasApiKeys] = useState<Record<string, boolean>>({ BINANCE: false, BITGET: false });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadPositions = useCallback(async (silent = false) => {
    if (!isAuthenticated) {
      setPositions([]);
      setSummary(null);
      if (!silent) setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await api.get('/positions');
      setPositions(res.data.positions || []);
      setSummary(res.data.summary || null);
    } catch (e: any) {
      if (!silent) setError(e?.response?.data?.error || e.message || '加载失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAuthenticated]);

  const loadApiKeyStatus = useCallback(async () => {
    try {
      const res = await api.get('/auto-trade/cex/key-status');
      setHasApiKeys({
        BINANCE: res.data?.binance?.bound === true,
        BITGET: res.data?.bitget?.bound === true,
      });
    } catch { /* non-critical */ }
  }, []);

  // Initial load
  useEffect(() => {
    loadPositions();
    loadApiKeyStatus();
  }, [loadPositions, loadApiKeyStatus]);

  // Auto-refresh every 10s
  useEffect(() => {
    timerRef.current = setInterval(() => {
      loadPositions(true);
      setCountdown(REFRESH_INTERVAL / 1000);
    }, REFRESH_INTERVAL);

    countdownRef.current = setInterval(() => {
      setCountdown(c => Math.max(0, c - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [loadPositions]);

  // Filter positions by tab
  const filteredPositions = positions.filter(p => {
    if (tab === 'ALL') return true;
    return p.exchange === tab;
  });

  const tabCount = (exchange: string) =>
    positions.filter(p => exchange === 'ALL' || p.exchange === exchange).length;

  const platformCount = new Set(positions.map(p => p.exchange)).size;

  // Close position
  const handleClose = async () => {
    if (!closeTarget) return;
    await api.post(`/positions/${closeTarget.id}/close`, { type: 'MARKET' });
    setCloseTarget(null);
    loadPositions(true);
  };

  // Edit SL
  const handleEditSL = async (slPrice: number) => {
    if (!slTarget) return;
    await api.post(`/positions/${slTarget.id}/sl`, { sl_price: slPrice });
    setSlTarget(null);
    loadPositions(true);
  };

  if (view === 'history') {
    return (
      <div className="pos-page">
        <PositionHistory onBack={() => setView('open')} />
      </div>
    );
  }

  return (
    <div className="pos-page">
      <div className="pos-page-title">My Positions</div>
      <div className="pos-page-sub">Real-time cross-platform position management</div>

      {/* Summary */}
      <PositionSummary
        totalPositions={summary?.total_positions ?? positions.length}
        totalUnrealizedPnl={summary?.total_unrealized_pnl ?? null}
        platformCount={platformCount}
      />

      {/* Tab bar */}
      <div className="pos-tab-bar">
        {([
          { key: 'ALL', label: 'All' },
          { key: 'HYPERLIQUID', label: 'Hyperliquid' },
          { key: 'BINANCE', label: 'Binance' },
          { key: 'BITGET', label: 'Bitget' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            className={`pos-tab ${tab === key ? 'active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
            <span className="pos-tab-count">{tabCount(key)}</span>
          </button>
        ))}
        {(['ASTER', 'LIGHTER', 'GMX'] as const).map(key => (
          <button key={key} className="pos-tab pos-tab-reserved" disabled>
            {key.charAt(0) + key.slice(1).toLowerCase()}
          </button>
        ))}
        <div className="pos-tab-spacer" />
        <button className="pos-tab" onClick={() => setView('history')}>History →</button>
      </div>

      {/* Section head */}
      <div className="pos-section-head">
        <span className="pos-section-title">Open Positions</span>
        <span className="pos-live-badge" onClick={() => { loadPositions(true); setCountdown(REFRESH_INTERVAL / 1000); }}>
          <span className="pos-live-dot" />
          Live · updates in {countdown}s
        </span>
      </div>

      {/* Content */}
      {error && <div className="pos-error">{error}</div>}

      {loading ? (
        <div className="pos-loading"><span className="pos-spinner" />Loading positions...</div>
      ) : filteredPositions.length === 0 ? (
        tab !== 'ALL' && ['ASTER', 'LIGHTER', 'GMX'].includes(tab) ? (
          <div className="pos-empty">
            <div className="pos-empty-icon">🔜</div>
            <div className="pos-empty-text">Coming Soon</div>
          </div>
        ) : (
          <div className="pos-empty">
            <div className="pos-empty-icon">📊</div>
            <div className="pos-empty-text">No open positions</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              Positions from copy trading will appear here
            </div>
          </div>
        )
      ) : (
        <div className="pos-list">
          {filteredPositions.map(pos => (
            <PositionCard
              key={pos.id}
              position={pos}
              hasApiKey={hasApiKeys[pos.exchange] ?? true}
              onClose={setCloseTarget}
              onEditSL={setSlTarget}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {closeTarget && (
        <ClosePositionModal
          position={closeTarget}
          onConfirm={handleClose}
          onCancel={() => setCloseTarget(null)}
        />
      )}
      {slTarget && (
        <EditSlModal
          position={slTarget}
          onConfirm={handleEditSL}
          onCancel={() => setSlTarget(null)}
        />
      )}
    </div>
  );
}
